import { Component, input, output, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonComponent } from '../button/button.component';
import { LoadingStateComponent } from '../loading-state/loading-state.component';
import { EmptyStateComponent } from '../empty-state/empty-state.component';
import type { NavIconName } from '../../../icons/nav-icon.model';

/**
 * Table Column Definition
 */
export interface TableColumn<T = any> {
  key: string;
  label: string;
  sortable?: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
  format?: (value: any, row: T) => string;
  render?: (value: any, row: T) => string;
}

/**
 * Sort Configuration
 */
export interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

/**
 * Pagination Configuration
 */
export interface PaginationConfig {
  page: number;
  pageSize: number;
  total: number;
}

/**
 * Data Table Component
 * 
 * Advanced data table with sorting, filtering, pagination, and selection.
 * Essential for listing Programs, Projects, and Demands.
 * 
 * @example
 * ```typescript
 * columns: TableColumn[] = [
 *   { key: 'name', label: 'Nome', sortable: true },
 *   { key: 'status', label: 'Status', sortable: true },
 *   { key: 'createdAt', label: 'Criado em', sortable: true, 
 *     format: (value) => new Date(value).toLocaleDateString() }
 * ];
 * 
 * data = signal<Program[]>([...]);
 * ```
 * 
 * ```html
 * <mg3p-data-table
 *   [columns]="columns"
 *   [data]="data()"
 *   [loading]="loading()"
 *   [sortable]="true"
 *   [selectable]="true"
 *   (sort)="onSort($event)"
 *   (rowClick)="onRowClick($event)"
 *   (selectionChange)="onSelectionChange($event)"
 * />
 * ```
 * 
 * Features:
 * - Column sorting (single and multi-column)
 * - Row selection (single and multi-select)
 * - Pagination
 * - Loading state
 * - Empty state
 * - Responsive (mobile cards)
 * - Keyboard navigation
 * - WCAG 2.1 Level AA compliant
 */
@Component({
  selector: 'mg3p-data-table',
  standalone: true,
  imports: [CommonModule, ButtonComponent, LoadingStateComponent, EmptyStateComponent],
  template: `
    <div class="data-table" [class.data-table--mobile]="isMobile()">
      <!-- Toolbar -->
      @if (showToolbar()) {
        <div class="data-table__toolbar">
          <div class="data-table__toolbar-left">
            @if (title()) {
              <h3 class="data-table__title">{{ title() }}</h3>
            }
            @if (selectedRows().length > 0) {
              <div class="data-table__toolbar-selection" role="status" aria-live="polite">
                <span class="data-table__selection-count">
                  {{ selectedRows().length }}
                  {{ selectedRows().length === 1 ? 'item selecionado' : 'itens selecionados' }}
                </span>
                <div class="data-table__toolbar-bulk">
                  <ng-content select="[toolbar-bulk]"></ng-content>
                </div>
              </div>
            }
          </div>

          <div class="data-table__toolbar-right">
            <div class="data-table__toolbar-actions">
              <ng-content select="[toolbar-actions]"></ng-content>
            </div>
          </div>
        </div>
      }

      <!-- Table container -->
      <div class="data-table__container">
        @if (loading()) {
          <mg3p-loading-state
            type="table"
            [count]="skeletonRows()"
            [columns]="skeletonColumns()"
            [message]="loadingMessage()"
          />
        } @else if (data().length === 0) {
          <mg3p-empty-state
            [iconName]="emptyIconName()"
            [title]="emptyTitle()"
            [message]="emptyMessage()"
            size="sm"
          >
            <ng-content select="[empty-actions]"></ng-content>
          </mg3p-empty-state>
        } @else {
          <!-- Desktop table -->
          @if (!isMobile()) {
            <table class="data-table__table" role="table">
              <thead class="data-table__thead">
                <tr class="data-table__row" role="row">
                  @if (selectable()) {
                    <th class="data-table__th data-table__th--checkbox" role="columnheader">
                      <input
                        type="checkbox"
                        class="data-table__checkbox"
                        [checked]="isAllSelected()"
                        [indeterminate]="isSomeSelected()"
                        (change)="toggleSelectAll()"
                        [attr.aria-label]="'Selecionar todos'"
                      />
                    </th>
                  }
                  
                  @for (column of columns(); track column.key) {
                    <th 
                      class="data-table__th"
                      [class.data-table__th--sortable]="column.sortable"
                      [class.data-table__th--sorted]="isSorted(column.key)"
                      [style.width]="column.width"
                      [style.text-align]="column.align || 'left'"
                      role="columnheader"
                      [attr.aria-sort]="getSortDirection(column.key)"
                    >
                      @if (column.sortable) {
                        <button
                          type="button"
                          class="data-table__sort-button"
                          (click)="toggleSort(column.key)"
                        >
                          {{ column.label }}
                          <span class="data-table__sort-icon" aria-hidden="true">
                            {{ getSortIcon(column.key) }}
                          </span>
                        </button>
                      } @else {
                        {{ column.label }}
                      }
                    </th>
                  }

                  @if (hasActions()) {
                    <th class="data-table__th data-table__th--actions" role="columnheader">
                      Ações
                    </th>
                  }
                </tr>
              </thead>

              <tbody class="data-table__tbody">
                @for (row of data(); track trackBy(row); let index = $index) {
                  <tr 
                    class="data-table__row"
                    [class.data-table__row--selected]="isRowSelected(row)"
                    [class.data-table__row--clickable]="clickable()"
                    (click)="onRowClicked(row)"
                    role="row"
                    [attr.aria-selected]="isRowSelected(row)"
                  >
                    @if (selectable()) {
                      <td class="data-table__td data-table__td--checkbox" role="cell">
                        <input
                          type="checkbox"
                          class="data-table__checkbox"
                          [checked]="isRowSelected(row)"
                          (change)="toggleRowSelection(row, $event)"
                          (click)="$event.stopPropagation()"
                          [attr.aria-label]="'Selecionar linha ' + (index + 1)"
                        />
                      </td>
                    }

                    @for (column of columns(); track column.key) {
                      <td 
                        class="data-table__td"
                        [style.text-align]="column.align || 'left'"
                        role="cell"
                      >
                        @if (column.render) {
                          <span [innerHTML]="column.render(getCellValue(row, column.key), row)"></span>
                        } @else if (column.format) {
                          {{ column.format(getCellValue(row, column.key), row) }}
                        } @else {
                          {{ getCellValue(row, column.key) }}
                        }
                      </td>
                    }

                    @if (hasActions()) {
                      <td class="data-table__td data-table__td--actions" role="cell">
                        <ng-content select="[row-actions]"></ng-content>
                      </td>
                    }
                  </tr>
                }
              </tbody>
            </table>
          } @else {
            <!-- Mobile cards -->
            <div class="data-table__cards">
              @for (row of data(); track trackBy(row); let index = $index) {
                <div 
                  class="data-table__card"
                  [class.data-table__card--selected]="isRowSelected(row)"
                  (click)="onRowClicked(row)"
                >
                  @if (selectable()) {
                    <div class="data-table__card-checkbox">
                      <input
                        type="checkbox"
                        class="data-table__checkbox"
                        [checked]="isRowSelected(row)"
                        (change)="toggleRowSelection(row, $event)"
                        (click)="$event.stopPropagation()"
                        [attr.aria-label]="'Selecionar item ' + (index + 1)"
                      />
                    </div>
                  }

                  <div class="data-table__card-content">
                    @for (column of columns(); track column.key) {
                      <div class="data-table__card-field">
                        <span class="data-table__card-label">{{ column.label }}:</span>
                        <span class="data-table__card-value">
                          @if (column.render) {
                            <span [innerHTML]="column.render(getCellValue(row, column.key), row)"></span>
                          } @else if (column.format) {
                            {{ column.format(getCellValue(row, column.key), row) }}
                          } @else {
                            {{ getCellValue(row, column.key) }}
                          }
                        </span>
                      </div>
                    }
                  </div>

                  @if (hasActions()) {
                    <div class="data-table__card-actions">
                      <ng-content select="[row-actions]"></ng-content>
                    </div>
                  }
                </div>
              }
            </div>
          }
        }
      </div>

      <!-- Pagination -->
      @if (pagination() && data().length > 0) {
        <div class="data-table__pagination">
          <div class="data-table__pagination-info">
            Mostrando {{ paginationStart() }} - {{ paginationEnd() }} de {{ pagination()!.total }}
          </div>

          <div class="data-table__pagination-controls">
            <mg3p-button
              variant="ghost"
              size="sm"
              (click)="goToPage(pagination()!.page - 1)"
              [disabled]="pagination()!.page === 1"
              [attr.aria-label]="'Página anterior'"
            >
              ←
            </mg3p-button>

            <span class="data-table__pagination-pages">
              Página {{ pagination()!.page }} de {{ totalPages() }}
            </span>

            <mg3p-button
              variant="ghost"
              size="sm"
              (click)="goToPage(pagination()!.page + 1)"
              [disabled]="pagination()!.page >= totalPages()"
              [attr.aria-label]="'Próxima página'"
            >
              →
            </mg3p-button>
          </div>
        </div>
      }
    </div>
  `,
  styles: [`
    .data-table {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-md);
    }

    /* Toolbar */
    .data-table__toolbar {
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      flex-wrap: wrap;
      gap: var(--spacing-md);
      padding: var(--spacing-md);
      background: var(--color-surface);
      border: 1px solid var(--color-border);
      border-radius: var(--radius-md);
    }

    .data-table__toolbar-left {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: var(--spacing-sm) var(--spacing-md);
      flex: 1;
      min-width: 0;
    }

    .data-table__toolbar-right {
      display: flex;
      align-items: flex-end;
      flex-shrink: 0;
    }

    .data-table__toolbar-actions {
      display: flex;
      flex-wrap: wrap;
      align-items: flex-end;
      justify-content: flex-end;
      gap: var(--spacing-sm);
    }

    .data-table__toolbar-selection {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: var(--spacing-sm);
      padding: var(--spacing-xs) var(--spacing-sm);
      border-radius: var(--radius-md);
      background: color-mix(in srgb, var(--color-primary) 10%, var(--color-surface));
      border: 1px solid color-mix(in srgb, var(--color-primary) 28%, transparent);
    }

    .data-table__toolbar-bulk {
      display: flex;
      align-items: center;
      gap: var(--spacing-xs);
    }

    .data-table__toolbar-bulk ::ng-deep .mg3p-button--danger {
      box-shadow: 0 1px 3px color-mix(in srgb, var(--color-pmdf-danger, #b91c1c) 35%, transparent);
    }

    .data-table__title {
      font-size: var(--font-size-lg);
      font-weight: var(--font-weight-semibold);
      color: var(--color-text-primary);
      margin: 0;
    }

    .data-table__selection-count {
      font-size: var(--font-size-sm);
      font-weight: var(--font-weight-medium);
      color: var(--color-primary);
      white-space: nowrap;
      font-weight: var(--font-weight-medium);
    }

    /* Container */
    .data-table__container {
      background: var(--color-surface);
      border: 1px solid var(--color-border);
      border-radius: var(--radius-md);
      overflow: hidden;
    }

    /* Table */
    .data-table__table {
      width: 100%;
      border-collapse: collapse;
    }

    .data-table__thead {
      background: var(--color-surface-secondary);
      border-bottom: 2px solid var(--color-border);
    }

    .data-table__th {
      padding: var(--spacing-sm) var(--spacing-md);
      font-size: var(--font-size-sm);
      font-weight: var(--font-weight-semibold);
      color: var(--color-text-secondary);
      text-align: left;
      white-space: nowrap;
    }

    .data-table__th--checkbox {
      width: 40px;
      padding: var(--spacing-sm);
    }

    .data-table__th--actions {
      width: 100px;
      text-align: right;
    }

    .data-table__th--sortable {
      cursor: pointer;
      user-select: none;
    }

    .data-table__sort-button {
      display: flex;
      align-items: center;
      gap: var(--spacing-xs);
      width: 100%;
      padding: 0;
      background: transparent;
      border: none;
      font: inherit;
      color: inherit;
      cursor: pointer;
      transition: color var(--transition-fast);
    }

    .data-table__sort-button:hover {
      color: var(--color-primary);
    }

    .data-table__sort-icon {
      font-size: var(--font-size-xs);
      opacity: 0.5;
    }

    .data-table__th--sorted .data-table__sort-icon {
      opacity: 1;
      color: var(--color-primary);
    }

    .data-table__tbody {
      background: var(--color-surface);
    }

    .data-table__row {
      border-bottom: 1px solid var(--color-border);
      transition: background var(--transition-fast);
    }

    .data-table__row--clickable {
      cursor: pointer;
    }

    .data-table__row--clickable:hover {
      background: var(--color-surface-hover);
    }

    .data-table__row--selected {
      background: var(--color-primary-light);
    }

    .data-table__td {
      padding: var(--spacing-sm) var(--spacing-md);
      font-size: var(--font-size-md);
      color: var(--color-text-primary);
    }

    .data-table__td--checkbox {
      padding: var(--spacing-sm);
    }

    .data-table__td--actions {
      text-align: right;
    }

    .data-table__checkbox {
      cursor: pointer;
    }

    /* Mobile cards */
    .data-table__cards {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-sm);
      padding: var(--spacing-md);
    }

    .data-table__card {
      display: flex;
      gap: var(--spacing-sm);
      padding: var(--spacing-md);
      background: var(--color-surface);
      border: 1px solid var(--color-border);
      border-radius: var(--radius-md);
      transition: all var(--transition-fast);
    }

    .data-table__card:hover {
      border-color: var(--color-primary);
      box-shadow: var(--shadow-sm);
    }

    .data-table__card--selected {
      background: var(--color-primary-light);
      border-color: var(--color-primary);
    }

    .data-table__card-checkbox {
      flex-shrink: 0;
    }

    .data-table__card-content {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xs);
    }

    .data-table__card-field {
      display: flex;
      gap: var(--spacing-sm);
    }

    .data-table__card-label {
      font-size: var(--font-size-sm);
      font-weight: var(--font-weight-medium);
      color: var(--color-text-secondary);
      min-width: 100px;
    }

    .data-table__card-value {
      font-size: var(--font-size-sm);
      color: var(--color-text-primary);
    }

    .data-table__card-actions {
      display: flex;
      gap: var(--spacing-xs);
      align-items: flex-start;
    }

    /* Pagination */
    .data-table__pagination {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: var(--spacing-md);
      background: var(--color-surface);
      border: 1px solid var(--color-border);
      border-radius: var(--radius-md);
    }

    .data-table__pagination-info {
      font-size: var(--font-size-sm);
      color: var(--color-text-secondary);
    }

    .data-table__pagination-controls {
      display: flex;
      align-items: center;
      gap: var(--spacing-sm);
    }

    .data-table__pagination-pages {
      font-size: var(--font-size-sm);
      color: var(--color-text-primary);
      padding: 0 var(--spacing-sm);
    }

    /* Mobile */
    @media (max-width: 768px) {
      .data-table__toolbar {
        flex-direction: column;
        align-items: stretch;
      }

      .data-table__pagination {
        flex-direction: column;
        gap: var(--spacing-sm);
      }
    }

    /* Reduced motion */
    @media (prefers-reduced-motion: reduce) {
      .data-table__row,
      .data-table__card,
      .data-table__sort-button {
        transition: none;
      }
    }
  `]
})
export class DataTableComponent<T = any> {
  // Inputs
  title = input<string>('');
  columns = input.required<TableColumn<T>[]>();
  data = input.required<T[]>();
  loading = input<boolean>(false);
  loadingMessage = input<string>('Carregando registros...');
  skeletonRows = input<number>(6);
  emptyIconName = input<NavIconName>('programs');
  emptyTitle = input<string>('Nenhum registro encontrado');
  emptyMessage = input<string>('Não há dados para exibir no momento.');
  sortable = input<boolean>(true);
  selectable = input<boolean>(false);
  clickable = input<boolean>(true);
  pagination = input<PaginationConfig | null>(null);
  showToolbar = input<boolean>(true);
  hasActions = input<boolean>(false);
  trackByKey = input<string>('id');

  // Outputs
  sort = output<SortConfig>();
  rowClick = output<T>();
  selectionChange = output<T[]>();
  pageChange = output<number>();

  // State
  sortConfig = signal<SortConfig | null>(null);
  selectedRows = signal<T[]>([]);
  isMobile = signal<boolean>(false);

  // Computed
  totalPages = computed(() => {
    const pg = this.pagination();
    return pg ? Math.ceil(pg.total / pg.pageSize) : 1;
  });

  skeletonColumns = computed(() => {
    let cols = this.columns().length;
    if (this.selectable()) cols += 1;
    if (this.hasActions()) cols += 1;
    return Math.max(cols, 2);
  });

  paginationStart = computed(() => {
    const pg = this.pagination();
    return pg ? (pg.page - 1) * pg.pageSize + 1 : 1;
  });

  paginationEnd = computed(() => {
    const pg = this.pagination();
    if (!pg) return this.data().length;
    return Math.min(pg.page * pg.pageSize, pg.total);
  });

  constructor() {
    this.isMobile.set(window.innerWidth <= 768);
  }

  trackBy(row: T): any {
    return (row as any)[this.trackByKey()];
  }

  getCellValue(row: T, key: string): any {
    return (row as any)[key];
  }

  isSorted(key: string): boolean {
    return this.sortConfig()?.key === key;
  }

  getSortDirection(key: string): string | null {
    const config = this.sortConfig();
    if (config?.key === key) {
      return config.direction === 'asc' ? 'ascending' : 'descending';
    }
    return null;
  }

  getSortIcon(key: string): string {
    const config = this.sortConfig();
    if (config?.key === key) {
      return config.direction === 'asc' ? '↑' : '↓';
    }
    return '↕';
  }

  toggleSort(key: string): void {
    const current = this.sortConfig();
    
    if (current?.key === key) {
      // Toggle direction
      const newDirection = current.direction === 'asc' ? 'desc' : 'asc';
      this.sortConfig.set({ key, direction: newDirection });
    } else {
      // New sort
      this.sortConfig.set({ key, direction: 'asc' });
    }

    this.sort.emit(this.sortConfig()!);
  }

  isRowSelected(row: T): boolean {
    return this.selectedRows().includes(row);
  }

  isAllSelected(): boolean {
    return this.data().length > 0 && 
           this.selectedRows().length === this.data().length;
  }

  isSomeSelected(): boolean {
    const selected = this.selectedRows().length;
    return selected > 0 && selected < this.data().length;
  }

  toggleRowSelection(row: T, event: Event): void {
    event.stopPropagation();
    
    const selected = this.selectedRows();
    const index = selected.indexOf(row);
    
    if (index >= 0) {
      this.selectedRows.set(selected.filter(r => r !== row));
    } else {
      this.selectedRows.set([...selected, row]);
    }

    this.selectionChange.emit(this.selectedRows());
  }

  toggleSelectAll(): void {
    if (this.isAllSelected()) {
      this.selectedRows.set([]);
    } else {
      this.selectedRows.set([...this.data()]);
    }

    this.selectionChange.emit(this.selectedRows());
  }

  onRowClicked(row: T): void {
    if (this.clickable()) {
      this.rowClick.emit(row);
    }
  }

  goToPage(page: number): void {
    const pg = this.pagination();
    if (!pg) return;
    
    if (page >= 1 && page <= this.totalPages()) {
      this.pageChange.emit(page);
    }
  }
}

// Made with Bob
